import { useEffect, useState } from "react";
import { useQueries } from "@tanstack/react-query";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { Failed, PaneSkeleton, useDiscountWrite } from "./DiscountParts";
import {
  cellKey, DISCOUNTS_COPY as T, fractionPct, MARKETS, noteStart, refreshSubscriptions, sendSubscription, subscriptionDone, subscriptionRemove,
  subscriptionSave, subscriptionsQuery, subscriptionTable, subscriptionUnchanged, type SubCell, type SubSend, type SubSlot, type Subscriptions,
} from "./discounts";
import { MARKET_LABEL, messageFor, personName, stamp, vatPct } from "./format";
import { NOTE_MAX } from "./strategies";
import { FormError, ResultLine, SectionHead, type Editor, type Result } from "./StrategyParts";

/**
 * The Subscriptions pane (Canvas v25 renderDiscountMatrix, v26's click-a-rate, C1 v1.22): products down, lanes across, each cell's
 * rate a button that opens its editor in place. The editor sets the standard rate of the lane or store row, and on an Amazon lane of
 * several marketplaces a rate per marketplace (owner ruling Q8: Subscribe and Save rates are typed here). Every write is C2's and
 * saves at once; the pane's one result line says what C2 answered. A lane where the product does not sell reads "—"; one where it
 * sells with no rate reads "No subscription offer" (v26).
 */
export function SubscriptionsPane({ areaName }: { areaName: string }) {
  const reads = useQueries({ queries: MARKETS.map((m) => subscriptionsQuery(m)) });
  const [result, setResult] = useState<Result>(null);
  const editor: Editor = { areaName, report: setResult, anchor: null };
  const head = <SectionHead title={T.subscriptions.title} desc={T.subscriptions.desc} />;
  const missing = reads.find((q) => q.data === undefined && q.error)?.error;
  const error = reads.find((q) => q.error)?.error;
  const retry = () => { for (const q of reads) void q.refetch(); };
  if (reads.some((q) => q.isPending) && !missing) return <section className="flex flex-col gap-2">{head}<PaneSkeleton /></section>;
  if (missing) return <section className="flex flex-col gap-2">{head}<Failed sentence={`${T.subscriptions.cannotLoad} ${messageFor(missing, areaName)}`} onRetry={retry} /></section>;
  const loaded = reads.map((q) => q.data).filter((d): d is Subscriptions => d !== undefined);
  const table = subscriptionTable(loaded);
  return (
    <section className="flex flex-col gap-2">
      {head}
      {error && <Failed sentence={T.subscriptions.notRefreshed} onRetry={retry} />}
      <ResultLine result={result} />
      <div className="flex flex-wrap gap-2">
        {loaded.map((r) => <Badge key={r.market} variant="secondary">{T.subscriptions.summary(MARKET_LABEL[r.market], r.set_sku_count, r.sku_count)}</Badge>)}
      </div>
      {/* T26 (X2, X3 2026-10-16): tiers and the max rate are deferred; every rate is a flat rate. */}
      <p className="text-sm text-ks-grey-1" data-tiers-deferred>{T.subscriptions.tiersDeferred}</p>
      {loaded.every((r) => r.set_count === 0) && <p className="text-sm text-ks-grey-1">{T.subscriptions.nothingSet}</p>}
      <Card><CardContent className="overflow-x-auto py-2">
        <Table>
          <TableHeader><TableRow><TableHead>{T.subscriptions.columns.product}</TableHead>{table.lanes.map((l) => <TableHead key={l.key}>{l.label}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {table.products.map((product) => (
              <TableRow key={product}>
                <TableCell className="align-top font-medium text-ks-ink">{product}</TableCell>
                {table.lanes.map((lane) => <TableCell key={lane.key} className="align-top"><SubscriptionCell cell={table.cells[cellKey(lane.key, product)]} editor={editor} /></TableCell>)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
      <p className="text-sm text-ks-grey-1">{T.subscriptions.note}</p>
    </section>
  );
}

/**
 * A cell: "—" where the product does not sell; else the rate (or "No subscription offer") as the toggle that opens and closes its
 * editor under it. Opening is open-to-read (AGENTS rule 11): who set a rate and the per-marketplace rates show only in the open cell,
 * so the toggle carries `data-nonmutating` and a view-only person keeps it; the boxes, Save and Remove inside stay unmarked and inert.
 */
function SubscriptionCell({ cell, editor }: { cell: SubCell | undefined; editor: Editor }) {
  const [open, setOpen] = useState(false);
  if (!cell) return <span className="text-ks-grey-1">{T.subscriptions.none}</span>;
  const shown = cell.standard ? T.subscriptions.off(vatPct(cell.standard)) : T.subscriptions.noOffer;
  return (
    <div className="flex flex-col items-start gap-1">
      <Button size="xs" variant={cell.standard ? "outline" : "ghost"} className={cell.standard ? undefined : "h-auto px-0 text-ks-grey-1"} aria-expanded={open}
        aria-label={T.editor.cellLabel(shown, cell.product, cell.laneLabel)} title={cell.standard ? undefined : cell.notSet ?? undefined} data-nonmutating
        onClick={() => setOpen(!open)}>{shown}</Button>
      {!open && cell.overrides.map((o) => <div key={o.marketplace} className="text-xs text-ks-grey-1">{T.subscriptions.override(o.marketplace, vatPct(o.rate))}</div>)}
      {open && <CellEditor cell={cell} editor={editor} onClose={() => setOpen(false)} />}
    </div>
  );
}

/** The open cell: the lane or store row's rate and note with Save, Remove (when a rate is set) and Close, who set it, then one row per
 * marketplace the lane offers. The boxes follow the read: after a save elsewhere they show what is set. */
export function CellEditor({ cell, editor, onClose }: { cell: SubCell; editor: Editor; onClose: () => void }) {
  const current = cell.own ? fractionPct(cell.own.rate) : "";
  const startNote = noteStart(cell.own);
  const [pct, setPct] = useState(current);
  const [note, setNote] = useState(startNote);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setPct(current); setNote(startNote); }, [current, startNote]);
  const write = useDiscountWrite(editor, sendSubscription, (answer, send: SubSend) => subscriptionDone(cell, send, answer), refreshSubscriptions);
  const save = () => {
    const checked = subscriptionSave(cell, null, pct, note);
    if (!checked.ok) { setError(checked.error); return; }
    setError(null);
    write.mutate(checked.value);
  };
  return (
    <div className="flex min-w-52 flex-col gap-1">
      <div className="flex items-center gap-1">
        <Input inputMode="decimal" className="h-7 w-16" autoFocus aria-label={T.editor.rateLabel(cell.product, cell.laneLabel)} value={pct} onChange={(e) => setPct(e.target.value)} />
        <span className="text-xs text-ks-grey-1">{T.editor.pctOff}</span>
      </div>
      <Input className="h-7 text-xs" maxLength={NOTE_MAX} aria-label={T.editor.noteLabel(cell.product, cell.laneLabel)} placeholder={T.editor.notePlaceholder} value={note} onChange={(e) => setNote(e.target.value)} />
      <div className="flex flex-wrap gap-1">
        <Button size="xs" onClick={save} disabled={write.isPending || subscriptionUnchanged(cell.own, pct, note)}>{T.editor.save}</Button>
        {cell.own && <Button size="xs" variant="outline" onClick={() => write.mutate(subscriptionRemove(cell, null))} disabled={write.isPending}>{T.editor.remove}</Button>}
        <Button size="xs" variant="outline" data-nonmutating onClick={onClose}>{T.editor.close}</Button>
      </div>
      <FormError error={error} />
      {cell.own && <span className="text-xs text-ks-grey-1">{T.editor.setBy(personName(cell.own.set_by), stamp(cell.own.set_at), cell.own.source)}</span>}
      {cell.slots.length > 0 && <span className="pt-1 text-xs font-semibold text-ks-ink">{T.editor.marketplaces}</span>}
      {cell.slots.map((slot) => <MarketplaceRate key={slot.marketplace_id} cell={cell} slot={slot} editor={editor} />)}
    </div>
  );
}

/** One marketplace of the lane: its own rate, or an empty box that reads "same as lane"; Save, and Remove on an own rate. */
function MarketplaceRate({ cell, slot, editor }: { cell: SubCell; slot: SubSlot; editor: Editor }) {
  const current = slot.own ? fractionPct(slot.own.rate) : "";
  const [pct, setPct] = useState(current);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setPct(current); }, [current]);
  const write = useDiscountWrite(editor, sendSubscription, (answer, send: SubSend) => subscriptionDone(cell, send, answer), refreshSubscriptions);
  const save = () => {
    const checked = subscriptionSave(cell, slot, pct, null);
    if (!checked.ok) { setError(checked.error); return; }
    setError(null);
    write.mutate(checked.value);
  };
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex flex-wrap items-center gap-1">
        <span className="w-24 text-xs text-ks-grey-1">{slot.marketplace_name}</span>
        <Input inputMode="decimal" className="h-7 w-14" aria-label={T.editor.rateLabel(cell.product, slot.marketplace_name)} value={pct} onChange={(e) => setPct(e.target.value)} />
        <span className="text-xs text-ks-grey-1">{slot.own || pct.trim() ? T.editor.pctOff : T.editor.sameAsLane}</span>
        <Button size="xs" onClick={save} disabled={write.isPending || subscriptionUnchanged(slot.own, pct, null)}>{T.editor.save}</Button>
        {slot.own && <Button size="xs" variant="outline" onClick={() => write.mutate(subscriptionRemove(cell, slot))} disabled={write.isPending}>{T.editor.remove}</Button>}
      </div>
      <FormError error={error} />
    </div>
  );
}
